import {useCallback} from 'react';

import {colors} from 'shells/colors/colors';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PlusThinIcon} from 'shells/icon/svg/plus-thin-icon';
import {Body} from 'shells/typography/body';

import {analyticsClient} from '../../../../../analytics-client';
import {appHistory} from '../../../../../history';

import type {ActionItem} from './types';

import styles from './command-palette.module.css';

interface Props {
    item: ActionItem;
    isActive: boolean;
    innerRef?: React.Ref<HTMLDivElement>;
    onExecuted?: () => void;
    /** Current search query text, passed to AI-superpowered actions */
    searchQuery?: string;
}

export function SearchResultAction(props: Props) {
    const {item, searchQuery, onExecuted} = props;
    const handleClick = useCallback(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_COMMAND_PALETTE_ACTION_EXECUTED, {
            actionId: item.id,
            label: item.label,
            section: item.section,
            isAiAction: item.isAiAction,
            query: searchQuery,
        });

        const result = item.getAction(searchQuery);
        if (typeof result === 'string') {
            appHistory.push(result);
        }
        if (onExecuted) {
            onExecuted();
        }
    }, [item, onExecuted, searchQuery]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleClick();
            }
        },
        [handleClick]
    );

    const isAi = item.isAiAction;

    return (
        <div
            ref={props.innerRef}
            className={props.isActive ? styles['result-row--active'] : styles['result-row']}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role='button'
            data-result-id={item.id}
        >
            <div
                className={isAi ? styles['result-row-icon--ai'] : styles['result-row-icon--action']}
            >
                {isAi ? (
                    // @ts-expect-error
                    <AiSparkleNewIcon size={14} color={colors.aiPurplePrimary} alignCenter={true} />
                ) : (
                    <PlusThinIcon size={14} color={colors.greenDk} alignCenter={true} />
                )}
            </div>
            <div className='flex flex-dir-col flex-grow min-width-0'>
                <Body size='normal' color='dark' truncate={{type: 'inline'}}>
                    {item.label}
                </Body>
            </div>
            <span className={isAi ? styles['result-row-badge--ai'] : styles['result-row-badge']}>
                {isAi ? 'AI' : 'Action'}
            </span>
        </div>
    );
}
